import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import {
    MdContentCopy,
    MdOutlineDelete,
    MdEdit,
    MdDragIndicator,
} from 'react-icons/md';

interface SortableOptionRowProps {
    id: string;
    option: any;
    index: number;
    onEdit: (index: number) => void;
    onToggleVisible: (index: number) => void;
    onDuplicate: (index: number) => void;
    onRemove: (index: number) => void;
}

export const SortableOptionRow = ({
    id,
    option,
    index,
    onEdit,
    onToggleVisible,
    onDuplicate,
    onRemove,
}: SortableOptionRowProps) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? '#F0F6F6' : undefined,
        borderBottom: '1px solid #dee2e6',
    };

    const qty = option.quantity != null ? Number(option.quantity) : null;
    const remaining =
        option.quantity_remaining != null
            ? Number(option.quantity_remaining)
            : null;
    const qtyDisplay =
        qty != null ? (remaining != null ? `${remaining}/${qty}` : `${qty}`) : '';

    return (
        <tr ref={setNodeRef} style={style}>
            <td
                className='ps-0'
                style={{ width: 32, verticalAlign: 'middle' }}
            >
                <span
                    {...attributes}
                    {...listeners}
                    style={{
                        cursor: isDragging ? 'grabbing' : 'grab',
                        color: '#888',
                        fontSize: 20,
                        display: 'inline-flex',
                        touchAction: 'none',
                    }}
                    title='Drag to reorder'
                    aria-label='Drag to reorder option'
                >
                    <MdDragIndicator />
                </span>
            </td>
            <td>
                <div className='d-flex align-items-start gap-2'>
                    {option.image && (
                        <img
                            src={option.image}
                            alt=''
                            className='rounded'
                            style={{
                                width: 48,
                                height: 48,
                                objectFit: 'cover',
                                flexShrink: 0,
                            }}
                        />
                    )}
                    <div>
                        <strong
                            className='text-primary d-block'
                            style={{ cursor: 'pointer' }}
                            onClick={() => onEdit(index)}
                        >
                            {option.name}
                        </strong>
                        {option.description && (
                            <small
                                className='text-muted d-block'
                                style={{ lineHeight: 1.3 }}
                            >
                                {option.description}
                            </small>
                        )}
                    </div>
                </div>
            </td>
            <td className='text-muted'>{option.option_question || ''}</td>
            <td className='text-center'>{qtyDisplay}</td>
            <td className='text-end'>
                ${Number(option.price || 0).toFixed(2)}
            </td>
            <td className='text-end pe-0'>
                <div className='d-flex align-items-center justify-content-end gap-2'>
                    <span
                        style={{
                            cursor: 'pointer',
                            color: '#01aeb4',
                            fontSize: '18px',
                        }}
                        title={
                            option.is_visible !== false
                                ? 'Hide option'
                                : 'Show option'
                        }
                        onClick={() => onToggleVisible(index)}
                    >
                        {option.is_visible !== false ? (
                            <FaRegEye />
                        ) : (
                            <FaRegEyeSlash />
                        )}
                    </span>
                    <span
                        style={{
                            cursor: 'pointer',
                            color: '#01aeb4',
                            fontSize: '18px',
                        }}
                        title='Edit option'
                        onClick={() => onEdit(index)}
                    >
                        <MdEdit />
                    </span>
                    <span
                        style={{
                            cursor: 'pointer',
                            color: '#01aeb4',
                            fontSize: '18px',
                        }}
                        title='Copy option'
                        onClick={() => onDuplicate(index)}
                    >
                        <MdContentCopy />
                    </span>
                    <span
                        style={{
                            cursor: 'pointer',
                            color: '#952e46',
                            fontSize: '18px',
                        }}
                        title='Delete option'
                        onClick={() => onRemove(index)}
                    >
                        <MdOutlineDelete />
                    </span>
                </div>
            </td>
        </tr>
    );
};
